import * as React from 'react';
import { ChangeEvent } from 'react';
import { useTranslation } from 'next-i18next';

import { TabComponentType, cn, config } from '@restworld/utils-common';

import { Checkbox } from '@restworld/ui-components';

/** Section of the privacy policy that explains the optional box (what is recorded, for how long, how to revoke). */
export const SIGNUP_TRACKING_INFO_URL = `${config.restworldPrivacyUrl}#pixel`;

/**
 * The signup boxes of the worker screens (CV upload, wizard, saved search): the required terms
 * box, the optional news and tracking box (only when `onMarketingChange` is given), and the
 * privacy notice line, which is information to read and never a box to accept.
 */
export const TermsAndConditions = ({
  isChecked,
  type = 'default',
  iserror,
  onCheckboxChange,
  testId,
  marketingChecked,
  onMarketingChange,
  ...rest
}: {
  onCheckboxChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  isChecked?: boolean;
  type?: TabComponentType;
  iserror?: boolean;
  testId?: string;
  marketingChecked?: boolean;
  onMarketingChange?: (checked: boolean) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 w-full">
      <Checkbox
        id={`terms-and-conditions-${type}`}
        variant="box"
        iserror={iserror}
        title={
          <TitleWrapper iserror={iserror}>
            <span>{t('common:terms_and_conditions.i_accept_the')} </span>
            <CustomLink href={config.workerTermsUrl} target="_blank" rel="noopener noreferrer" iserror={iserror}>
              {t('common:terms_and_conditions.terms_and_conditions_of_the_service')}
            </CustomLink>
          </TitleWrapper>
        }
        isChecked={isChecked}
        mode="clickable"
        onChange={onCheckboxChange}
        testId={testId}
        {...rest}
      />
      {onMarketingChange && (
        <Checkbox
          id={`marketing-and-tracking-${type}`}
          variant="box"
          title={
            <TitleWrapper>
              <SignupMarketingConsentText />
            </TitleWrapper>
          }
          isChecked={!!marketingChecked}
          mode="clickable"
          onChange={(event: ChangeEvent<HTMLInputElement>) => onMarketingChange(event.target.checked)}
          testId={testId ? `${testId}-marketing` : 'signup-marketing-and-tracking'}
        />
      )}
      <SignupPrivacyLine className="pl-[1.625rem]" />
    </div>
  );
};

/** Label of the optional box: news and tips from Restworld, then «Dettagli» (the open tracking is explained there). */
export const SignupMarketingConsentText = () => {
  const { t } = useTranslation();
  return (
    <>
      <span>{t('common:terms_and_conditions.marketing_and_tracking')} </span>
      <a
        href={SIGNUP_TRACKING_INFO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-400 underline underline-offset-4"
      >
        {t('common:terms_and_conditions.how_it_works')}
      </a>
    </>
  );
};

/** «Come usiamo i tuoi dati: informativa privacy»: a line with a link, not a box. */
export const SignupPrivacyLine = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  return (
    <p className={cn('text-gray-500 font-light leading-6 text-sm', className)} data-cy="signup-privacy-line">
      {t('common:terms_and_conditions.privacy_line')}{' '}
      <a
        href={config.restworldPrivacyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-4"
      >
        {t('common:terms_and_conditions.privacy_link')}
      </a>
    </p>
  );
};

const TitleWrapper = ({ iserror, className, ...props }: any) => (
  <p
    className={cn(
      'text-gray-500 font-light leading-6 text-sm w-full inline-block',
      iserror ? 'text-red-500' : '',
      className
    )}
    {...props}
  />
);
const CustomLink = ({ iserror, className, ...props }: any) => (
  <a className={cn(!iserror ? 'underline underline-offset-4' : '', className)} {...props} />
);
